import React from 'react';

import * as ArchiveActions from 'archive/ArchiveActions';
import ArchiveButton from 'archive/components/buttons/ArchiveButton';
import UnarchiveButton from 'archive/components/buttons/UnarchiveButton';
import isArchived from 'archive/isArchived';
import * as AuditActions from 'audit/AuditActions';
import {
  EXPORT_AUDIT_LOG_DISPLAY_STRING,
  EXPORT_LOCATION_HISTORY_ERROR_TOOLTIP,
  EXPORT_LOCATION_HISTORY_DISPLAY_STRING,
  EXPORT_PLATE_HISTORY_DISPLAY_STRING,
  EXPORT_PLATE_HISTORY_ERROR_TOOLTIP,
  EXPORT_SAMPLE_CONTAINER_HISTORY_DISPLAY_STRING,
} from 'audit/AuditConstants';
import {ProcessesFeature, V2RequestsFeature} from 'auth/capabilities/CapabilityFeatureConstants';
import {isFeatureEnabled} from 'auth/feature-enablement/FeatureStatus';
import BenchlingConfig from 'BenchlingConfig';
import {Storable, StorableWithPermissions} from 'constants/SampleConstants';
import {Tag} from 'folder-items/FileConstants';
import {ActionOrigin} from 'graphql-client/__generated/graphqlGlobalTypes';
import CICODropdown from 'inventory/check-in-check-out/CICODropdown';
import PrintLabelButton from 'label-printing/PrintLabelButton';
import ExportDropdown from 'nav/expanded/folder-toolbar/ExportDropdown';
import CreateRequestV2SubmissionShortcutButton from 'processes/requests_v2/dashboard/CreateRequestV2SubmissionShortcutButton';
import CreateProcessButton from 'processes/tables/create-process-button/CreateProcessButton';
import DeleteContainerContentsButton from 'samples/containers/DeleteContainerContentsButton';
import * as StorableActions from 'samples/StorableActions';
import {bulkUpdateTagsWithDataMods} from 'samples/StorableActions';
import TagEditorDropdown from 'schemas/tags/tag-editor/TagEditorDropdown';
import {getTagsToSet} from 'schemas/tags/TagUtil';
import AddToStudiesButton from 'studies/studyable/AddToStudiesButton';
import {isItemStudyable} from 'studies/studyable/StudyableUtils';
import GridCellKey from 'test/lib/cypress/shared/GridCellKey';
import {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import LegacyDropdownMenuItem from 'ui-library/dropdowns/LegacyDropdownMenuConstants';
import licenses from 'user/Licenses';
import APIUtil from 'util/APIUtil';
import AddToWorklistButton from 'worklists/AddToWorklistButton';
import {apiIdHasWorklistType} from 'worklists/WorklistUtil';

import MoveStorablesButton from './MoveStorablesButton';

interface Props {
  actionOrigin: ActionOrigin;
  buttonStyle: ButtonStyle;
  isLoading?: boolean;
  storables: Array<StorableWithPermissions> | null;
  selectedIds: Array<string>;
  extraExportMenuItems?: Array<LegacyDropdownMenuItem>;
  parentStorableForMove?: Storable;
  hideCheckInCheckOut?: boolean;
  hideMove?: boolean;
  hideArchive?: boolean;
}

export default class StorableToolbarContents extends React.Component<Props> {
  private _getSelectedStorables = (): Array<StorableWithPermissions> =>
    (this.props.storables ?? []).filter(({id}) => this.props.selectedIds.includes(id));

  private _getSelectedCount = (filterFn: (storable: StorableWithPermissions) => boolean): number => {
    if (this.props.isLoading || this.props.storables == null) {
      return 0;
    }
    return this.props.storables.filter(
      (storable) => this.props.selectedIds.includes(storable.id) && filterFn(storable)
    ).length;
  };

  private _getNumContainers = (): number =>
    this._getSelectedCount((storable) => APIUtil.isContainer(storable.id));

  private _getNumPlates = (): number => this._getSelectedCount((storable) => APIUtil.isPlate(storable.id));

  private _getNumLocations = (): number =>
    this._getSelectedCount((storable) => APIUtil.isLocation(storable.id));

  private _shouldDisplayExportPlateHistoryReport = (): boolean => {
    if (this.props.storables == null || !licenses.hasEnterprisePlan()) {
      return false;
    }
    return this.props.storables.some((storable) => APIUtil.isPlate(storable.id));
  };

  private _handleExportContainersToCSV = (): void => {
    StorableActions.exportContainersToCSV(this._getSelectedStorables().map((storable) => storable.id));
  };

  private _handleExportAuditLogs = (): void => {
    AuditActions.exportAuditLogs(this._getSelectedStorables().map((storable) => storable.id));
  };

  private _handleExportPlateHistoryReport = (): void => {
    AuditActions.exportPlateHistoryReport(this._getSelectedStorables().map((storable) => storable.id));
  };

  private _handleExportSampleContainerHistoryReport = (): void => {
    AuditActions.exportSampleContainerHistory(this._getSelectedStorables().map((storable) => storable.id));
  };

  private _handleExportLocationHistoryReport = (): void => {
    AuditActions.exportLocationHistoryReport(this._getSelectedStorables().map((storable) => storable.id));
  };

  private getPlateHistoryReportTooltipText = (): string =>
    this._getNumPlates() !== 1 || this._getSelectedStorables().length !== 1
      ? EXPORT_PLATE_HISTORY_ERROR_TOOLTIP
      : '';

  private getSampleContainerHistoryReportTooltipText = (
    hasAllContainers: boolean,
    exceedsLimit: boolean
  ): string => {
    if (!hasAllContainers) {
      return 'Reports can only be exported for containers.';
    }

    if (exceedsLimit) {
      return `Please select at most ${
        BenchlingConfig.AUDITING_MAX_NUM_SAMPLE_CONTAINER_HISTORY_REPORT_PER_EXPORT
      } containers (${this._getNumContainers()} selected).`;
    }
    return '';
  };

  private _getExportMenuItems = (): Array<Array<LegacyDropdownMenuItem>> => {
    const selectedStorables = this._getSelectedStorables();
    const numSelected = selectedStorables.length;
    const numContainers = this._getNumContainers();
    const numLocations = this._getNumLocations();
    const hasAllContainers = numSelected > 0 && numContainers === numSelected;
    const hasAllLocations = numSelected > 0 && numLocations === numSelected;
    const exceedsSampleContainerHistoryReportLimit =
      numSelected > BenchlingConfig.AUDITING_MAX_NUM_SAMPLE_CONTAINER_HISTORY_REPORT_PER_EXPORT;

    return [
      [
        {
          name: 'Export containers to CSV',
          handleOnClick: this._handleExportContainersToCSV,
          disabled: !hasAllContainers || numSelected === 0,
          visible: BenchlingConfig.ENABLE_EXPORTS,
          tooltipText: !hasAllContainers || numSelected === 0 ? 'Please select containers' : undefined,
        },
        ...(this.props.extraExportMenuItems || []),
        {
          name: EXPORT_AUDIT_LOG_DISPLAY_STRING,
          handleOnClick: this._handleExportAuditLogs,
          disabled: numSelected === 0,
          visible: licenses.hasEnterprisePlan(),
          dataTestAttrs: {component: 'StorableToolbarContents', element: 'exportAuditLogs'},
        },
        {
          name: EXPORT_PLATE_HISTORY_DISPLAY_STRING,
          handleOnClick: this._handleExportPlateHistoryReport,
          disabled: this._getNumPlates() !== 1 || numSelected !== 1,
          visible: this._shouldDisplayExportPlateHistoryReport(),
          tooltipText: this.getPlateHistoryReportTooltipText(),
          dataTestAttrs: {component: 'StorableToolbarContents', element: 'exportPlateHistory'},
        },
        {
          name: EXPORT_SAMPLE_CONTAINER_HISTORY_DISPLAY_STRING,
          handleOnClick: this._handleExportSampleContainerHistoryReport,
          disabled: !hasAllContainers || numSelected === 0 || exceedsSampleContainerHistoryReportLimit,
          visible: licenses.hasEnterprisePlan(),
          tooltipText: this.getSampleContainerHistoryReportTooltipText(
            hasAllContainers,
            exceedsSampleContainerHistoryReportLimit
          ),
          dataTestAttrs: {component: 'StorableToolbarContents', element: 'exportSampleContainerHistory'},
        },
        {
          name: EXPORT_LOCATION_HISTORY_DISPLAY_STRING,
          handleOnClick: this._handleExportLocationHistoryReport,
          disabled: !hasAllLocations || numSelected === 0,
          visible: licenses.hasEnterprisePlan() && numLocations > 0,
          tooltipText: !hasAllLocations ? EXPORT_LOCATION_HISTORY_ERROR_TOOLTIP : '',
          dataTestAttrs: {component: 'StorableToolbarContents', element: 'exportSampleContainerHistory'},
        },
        {
          name: 'Copy barcodes',
          clipboardData: selectedStorables.map((storable) => storable.barcode).join('\n'),
          disabled: numSelected === 0,
          dataTestAttrs: {component: 'StorableToolbarContents', key: GridCellKey.COPY_BARCODES},
        },
      ],
    ];
  };

  private _onBulkEditSubmit = (newTag: Tag, selectedStorableIds: Array<string>): Promise<void> => {
    const tagsToSet = getTagsToSet([newTag]);
    return bulkUpdateTagsWithDataMods(selectedStorableIds, tagsToSet);
  };

  private _shouldEnableTagEditorDropdown = (selectedStorables: Array<StorableWithPermissions>): boolean =>
    selectedStorables &&
    Boolean(selectedStorables.length) &&
    selectedStorables.every(
      (storable) =>
        storable.permissions.writable &&
        storable.storableSchema?.id === selectedStorables[0].storableSchema?.id
    );

  render = (): JSX.Element | null => {
    if (this.props.isLoading) {
      return null;
    }

    // Listed storables info
    const numContainers = (this.props.storables ?? []).filter((storable) =>
      APIUtil.isContainer(storable.id)
    ).length;
    const allStorablesAreContainers = numContainers === this.props.storables?.length;

    // Selected storables info
    const selectedStorables = this._getSelectedStorables();
    const numSelected = selectedStorables.length;
    const hasSelected = numSelected > 0;
    const hasSelectedOnlyWritable =
      this._getSelectedCount((storable: Storable) => storable.permissions.writable) === numSelected;
    const hasSelectedSomeWorklistTypes = this.props.storables?.some((storable) =>
      apiIdHasWorklistType(storable.id)
    );

    // Selected storables archive info
    const hasSelectedOnlyNotArchived = selectedStorables.every((storable) => !isArchived(storable));
    const hasSelectedOnlyArchived = selectedStorables.every((storable) => isArchived(storable));
    const hasSelectedOnlyArchivable =
      hasSelectedOnlyNotArchived &&
      this._getSelectedCount((storable: Storable) => Boolean(storable.permissions.archivable)) ===
        numSelected;
    const hasSelectedOnlyUnarchivable =
      hasSelectedOnlyArchived &&
      this._getSelectedCount((storable: Storable) => Boolean(storable.permissions.unarchivable)) ===
        numSelected;
    const hasSelectedOnlyStudyables =
      BenchlingConfig.STUDIES_ENABLE &&
      hasSelected &&
      selectedStorables.every((storable) => isItemStudyable(storable));

    return (
      <div className="buttonGroup">
        <TagEditorDropdown
          disabled={!this._shouldEnableTagEditorDropdown(selectedStorables)}
          results={selectedStorables}
          onSubmit={this._onBulkEditSubmit}
          buttonStyle={this.props.buttonStyle}
          actionOrigin={this.props.actionOrigin}
        />

        {!this.props.hideCheckInCheckOut && (
          <CICODropdown
            actionSource={this.props.actionOrigin}
            buttonStyle={this.props.buttonStyle}
            items={selectedStorables}
          />
        )}
        {!this.props.hideMove && (
          <MoveStorablesButton
            buttonStyle={this.props.buttonStyle}
            storables={selectedStorables}
            defaultParentStorable={this.props.parentStorableForMove}
          />
        )}
        {allStorablesAreContainers && hasSelectedOnlyWritable && (
          <DeleteContainerContentsButton
            buttonStyle={this.props.buttonStyle}
            disabled={!hasSelected}
            containerIds={selectedStorables.map((storable) => storable.id)}
            actionOrigin={this.props.actionOrigin}
          />
        )}
        <PrintLabelButton
          buttonStyle={this.props.buttonStyle}
          disabled={!hasSelected}
          itemIds={selectedStorables.map((storable) => storable.id)}
        />
        {isFeatureEnabled({feature: ProcessesFeature.PROCESSES_CREATE}) && (
          <div className="buttonGroup">
            <CreateProcessButton
              buttonStyle={this.props.buttonStyle}
              buttonSize={ButtonSize.SMALL}
              items={selectedStorables}
              disabled={!hasSelected}
              sourceType="storables_sidepanel"
            />
          </div>
        )}
        {isFeatureEnabled({feature: V2RequestsFeature.V2_REQUESTS_CREATE}) &&
          BenchlingConfig.REQUESTS_V2_ENABLE && (
            <CreateRequestV2SubmissionShortcutButton
              disabled={!hasSelected}
              items={selectedStorables}
              actionOrigin={this.props.actionOrigin}
              buttonStyle={this.props.buttonStyle}
            />
          )}
        {BenchlingConfig.STUDIES_ENABLE && (
          <AddToStudiesButton
            buttonStyle={this.props.buttonStyle}
            itemIds={selectedStorables.map((storable) => storable.id)}
            disabled={!hasSelectedOnlyStudyables}
          />
        )}
        {BenchlingConfig.WORKLISTS_ENABLE_WORKLISTS && hasSelectedSomeWorklistTypes && (
          <AddToWorklistButton
            buttonStyle={this.props.buttonStyle}
            ids={selectedStorables.map(({id}) => id)}
            refreshOptions={{refreshRecentWorklists: true}}
          />
        )}
        {(() => {
          if (this.props.hideArchive) {
            return null;
          }
          if (!hasSelected) {
            return (
              <ArchiveButton
                buttonStyle={this.props.buttonStyle}
                disabled={true}
                items={[]}
                tooltipTitle="Select items to archive"
                onArchive={ArchiveActions.bulkArchiveStorablesWithDataMods}
              />
            );
          } else if (hasSelectedOnlyNotArchived && hasSelectedOnlyArchivable) {
            return (
              <ArchiveButton
                buttonStyle={this.props.buttonStyle}
                items={selectedStorables}
                onArchive={ArchiveActions.bulkArchiveStorablesWithDataMods}
              />
            );
          } else if (hasSelectedOnlyUnarchivable) {
            return (
              <UnarchiveButton
                buttonStyle={this.props.buttonStyle}
                itemIds={selectedStorables.map((storable) => storable.id)}
                onUnarchive={(itemIds: Array<string>, _) =>
                  ArchiveActions.bulkUnarchiveStorablesWithDataMods(itemIds)
                }
              />
            );
          }
          return (
            <ArchiveButton
              buttonStyle={this.props.buttonStyle}
              disabled={true}
              items={[]}
              tooltipTitle="You do not have permission to archive some of the selected items"
              onArchive={ArchiveActions.bulkArchiveStorablesWithDataMods}
            />
          );
        })()}
        <ExportDropdown
          buttonStyle={this.props.buttonStyle}
          disabled={
            !(hasSelected || (this.props.extraExportMenuItems && this.props.extraExportMenuItems.length > 0))
          }
          menuItems={this._getExportMenuItems()}
        />
      </div>
    );
  };
}
